import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, Play, Award, X } from "lucide-react";
import api, { embedUrl } from "@/lib/api";
import SEO from "@/components/SEO";
import { FadeUp, Eyebrow, LineReveal } from "@/components/Motion";
import ProjectCard, { CategoryChip } from "@/components/ProjectCard";

export default function ProjectDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [playing, setPlaying] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  useEffect(() => {
    setP(null);
    setNotFound(false);
    setPlaying(false);
    api.get(`/projects/${slug}`).then((r) => setP(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-6 pt-16" data-testid="project-not-found">
        <h1 className="font-display text-3xl font-bold">Project not found</h1>
        <Link to="/" className="rounded-full bg-ink px-6 py-3 text-sm font-semibold text-cream dark:bg-cream dark:text-ink">Back to work</Link>
      </div>
    );
  }

  if (!p) {
    return <div className="flex min-h-[70vh] items-center justify-center pt-16 text-muted-foreground" data-testid="project-loading">Loading…</div>;
  }

  const embed = embedUrl(p.trailer_url);

  return (
    <div data-testid={`project-page-${p.slug}`}>
      <SEO
  title={p.title}
  description={
    (
      p.tagline ||
      p.synopsis ||
      "Explore this Incolour Films production, including project details, credits and production information."
    ).slice(0, 160)
  }
  image={p.poster}
  path={`/projects/${p.slug}`}
  type="website"
  jsonLd={{
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: p.title,
    description:
      p.synopsis ||
      p.tagline ||
      "An Incolour Films production.",
    image: p.poster,
    url: `https://incolourfilms.ca/projects/${p.slug}`,
    creator: {
      "@type": "Organization",
      name: "Incolour Films",
      url: "https://incolourfilms.ca",
    },
    ...(p.trailer_url
      ? {
          video: {
            "@type": "VideoObject",
            name: `${p.title} Trailer`,
            description:
              p.synopsis ||
              p.tagline ||
              `Trailer for ${p.title}.`,
            thumbnailUrl: p.poster ? [p.poster] : undefined,
            embedUrl: embedUrl(p.trailer_url),
            uploadDate: (p.created_at || "").slice(0, 10),
          },
        }
      : {}),
  }}
/>
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-[85svh] items-end overflow-hidden bg-ink pt-16 text-cream">
        <motion.img src={p.poster} alt={p.title} style={{ y: bgY }} className="absolute inset-0 h-[115%] w-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/30" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
          <Link to={p.category === "service" ? "/service" : "/original"} data-testid="project-back-link" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-cream/70 transition-colors hover:text-orange">
            <ArrowLeft size={14} /> {p.category === "service" ? "Service Production" : "Original Work"}
          </Link>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <CategoryChip category={p.category} />
            <span className="rounded-full bg-cream/15 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.18em] text-cream backdrop-blur">
              {p.type} · {p.year}
            </span>
            {p.client && (
              <span className="rounded-full bg-cream/15 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.18em] text-cream backdrop-blur">
                Client — {p.client}
              </span>
            )}
            {p.imdb_url && (
              <a
                href={p.imdb_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="project-imdb-link"
                className="rounded-full bg-cream/15 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.18em] text-cream backdrop-blur transition-colors hover:bg-orange"
              >
                IMDb
              </a>
            )}
          </div>
          {p.credits?.find((c) => /starring|cast/i.test(c.role)) && (
            <FadeUp delay={0.15}>
              <p className="mt-4 text-sm text-cream/80" data-testid="project-starring">
                Starring <span className="font-semibold text-cream">{p.credits.find((c) => /starring|cast/i.test(c.role)).name}</span>
              </p>
            </FadeUp>
          )}
          <h1 className="mt-5 max-w-4xl font-display text-4xl font-extrabold leading-[0.98] tracking-tight sm:text-6xl" data-testid="project-title">
            <LineReveal lines={[p.title]} />
          </h1>
          {p.tagline && (
            <FadeUp delay={0.25}>
              <p className="mt-6 max-w-2xl font-serif text-lg italic leading-relaxed text-cream/85 sm:text-xl">{p.tagline}</p>
            </FadeUp>
          )}
          {p.trailer_url && (
            <FadeUp delay={0.35}>
              <button
                onClick={() => setPlaying(true)}
                data-testid="project-play-trailer-btn"
                className="mt-8 flex items-center gap-3 rounded-full bg-orange px-6 py-3.5 text-sm font-semibold text-cream transition-transform hover:scale-[1.04]"
              >
                <Play size={14} className="fill-cream" /> Play trailer
              </button>
            </FadeUp>
          )}
        </div>
      </section>

      {/* TRAILER EMBED */}
      {playing && embed && (
        <section className="bg-ink px-4 pb-16 sm:px-6 lg:px-8" data-testid="trailer-embed-section">
          <div className="mx-auto aspect-video max-w-5xl overflow-hidden rounded-2xl border border-cream/15">
            <iframe
              src={`${embed}?autoplay=1`}
              title={`${p.title} trailer`}
              className="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </section>
      )}

      {/* SYNOPSIS */}
      <section className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-24" data-testid="synopsis-section">
        <div className="lg:col-span-4">
          <Eyebrow>Synopsis</Eyebrow>
          {p.awards?.length > 0 && (
            <div className="mt-8">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Recognition</p>
              <ul className="mt-4 space-y-3">
                {p.awards.map((a, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <Award size={15} className="mt-0.5 shrink-0 text-orange" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="lg:col-span-8">
          <FadeUp>
            <p className="text-lg leading-relaxed sm:text-xl">{p.synopsis}</p>
          </FadeUp>
          {p.director_note && (
            <FadeUp delay={0.15} className="mt-10">
              <blockquote className="rounded-2xl border border-border bg-lavender/20 p-8 dark:bg-lavender/10">
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Director's note{p.director ? ` — ${p.director}` : ""}</p>
                <p className="mt-4 font-serif text-xl italic leading-relaxed sm:text-2xl">“{p.director_note}”</p>
              </blockquote>
            </FadeUp>
          )}
        </div>
      </section>

      {/* STILLS */}
      {p.stills?.length > 0 && (
        <section className="border-y border-border bg-cream2 py-16 dark:bg-card lg:py-24" data-testid="stills-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Eyebrow>Stills</Eyebrow>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {p.stills.map((s, i) => (
                <FadeUp key={i} delay={i * 0.08}>
                  <button
                    onClick={() => setLightbox(s)}
                    data-testid={`still-${i}`}
                    className="group block w-full overflow-hidden rounded-xl border border-border"
                    aria-label={`Open still ${i + 1}`}
                  >
                    <img src={s} alt={`${p.title} still ${i + 1}`} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                  </button>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CREDITS */}
      {p.credits?.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24" data-testid="credits-section">
          <Eyebrow>Credits</Eyebrow>
          <div className="mt-8 grid gap-x-10 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
            {p.credits.map((c, i) => (
              <FadeUp key={i} delay={i * 0.04}>
                <div className="border-t-2 border-ink pt-4 dark:border-cream/30">
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-orange">{c.role}</p>
                  <p className="mt-2 font-display text-lg font-bold tracking-tight">{c.name}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </section>
      )}

      {/* RELATED */}
      {p.related?.length > 0 && (
        <section className="border-t border-border bg-cream2 py-16 dark:bg-card lg:py-24" data-testid="related-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Eyebrow>Related Work</Eyebrow>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {p.related.map((r, i) => (
                <ProjectCard key={r.id} p={r} i={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* LIGHTBOX */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/90 p-4 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
          data-testid="stills-lightbox"
          role="dialog"
          aria-label="Still viewer"
        >
          <button className="absolute right-5 top-5 rounded-full border border-cream/30 p-2 text-cream" aria-label="Close" data-testid="lightbox-close-btn">
            <X size={18} />
          </button>
          <motion.img
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            src={lightbox}
            alt="Production still"
            className="max-h-[85vh] max-w-full rounded-lg object-contain"
          />
        </div>
      )}
    </div>
  );
}
